// Globals
import { useCallback, useEffect, useMemo } from 'react';

// Components
import BidsList from '../../components/BidsList/BidsList';

// Modules
import { propertiesSelector } from '@/modules/Properties';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  getBidsList,
  resetBiddingState,
  bidsListSelector,
  biddingIsLoadingSelector,
  biddingPaginationSelector,
  biddingErrorSelector,
} from '../../feature';

// Helpers
import { getEntityId } from '@/utils';

interface BidsListContainerProps {
  propertyId?: string;
}

const BidsListContainer = ({ propertyId: propPropertyId }: BidsListContainerProps = {}) => {
  const dispatch = useAppDispatch();
  const bidsList = useAppState(bidsListSelector);
  const isLoading = useAppState(biddingIsLoadingSelector);
  const pagination = useAppState(biddingPaginationSelector);
  const error = useAppState(biddingErrorSelector);
  const { property } = useAppState(propertiesSelector);

  const propertyId = propPropertyId ?? getEntityId(property?.PK ?? '');
  const ownerName = property?.property_owner?.full_name ?? property?.owners?.[0]?.full_name ?? undefined;

  const items = useMemo(() => bidsList?.items ?? [], [bidsList?.items]);
  const hasMore = useMemo(() => !!pagination?.next, [pagination?.next]);
  const totalCount = useMemo(() => bidsList?.items_count, [bidsList?.items_count]);

  useEffect(() => {
    if (!propertyId) {
      dispatch(resetBiddingState());
      return;
    }

    dispatch(
      getBidsList({
        propertyId,
        limit: 10,
        with_co_bidders: true,
      }),
    );
  }, [dispatch, propertyId]);

  useEffect(() => {
    return () => {
      dispatch(resetBiddingState());
    };
  }, [dispatch]);

  const handleLoadMore = useCallback(() => {
    if (!pagination?.next || isLoading || !propertyId) {
      return;
    }

    dispatch(
      getBidsList({
        propertyId,
        cursor: pagination.next || undefined,
        limit: 10,
        with_co_bidders: true,
      }),
    );
  }, [dispatch, pagination?.next, isLoading, propertyId]);

  if (!propertyId) {
    return null;
  }

  return (
    <BidsList
      items={items}
      isLoading={isLoading}
      hasMore={hasMore}
      onLoadMore={handleLoadMore}
      totalCount={totalCount}
      ownerName={ownerName}
      hasError={!!error}
    />
  );
};

export default BidsListContainer;
